import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ProviderPerformanceCharts from './admin/ProviderPerformanceCharts';
// import FeatureFlags from './admin/FeatureFlags'; // Archived July 2026
import RateLimitConfig from './admin/RateLimitConfig';
import PolicyConfigDashboard from './admin/PolicyConfigDashboard';
import QueueDashboard from './admin/QueueDashboard';
import BiweeklyRotationDashboard from './admin/BiweeklyRotationDashboard';
// import AILearningDashboard from './admin/AILearningDashboard'; // Archived July 2026
import RejectionAnalytics from './admin/RejectionAnalytics';
import SimulationDashboard from './admin/SimulationDashboard';
import EntryScoreDistribution from './admin/EntryScoreDistribution';

type TabType =
  | 'overview'
  | 'performance'
  | 'features'
  | 'rate-limits'
  | 'policy'
  | 'selectors'
  | 'queue'
  | 'rotation'
  // | 'ai-learning' // Archived July 2026
  | 'rejection-analytics'
  | 'entry-scores'
  | 'simulation';

/**
 * Admin Console Component
 * Admin-only dashboard with system controls and status
 */
const AdminConsole: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<any | null>(null);
  const [tradingSuccessRate, setTradingSuccessRate] = useState<any | null>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Define tabs array before it's used in getInitialTab
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '' },
    { id: 'performance', label: 'Performance', icon: '' },
    { id: 'features', label: 'Feature Flags', icon: '' },
    { id: 'rate-limits', label: 'Rate Limits', icon: '' },
    { id: 'policy', label: 'Policy Config', icon: '' },
    { id: 'selectors', label: 'Selectors', icon: '' },
    { id: 'queue', label: 'Queue', icon: '' },
    { id: 'rotation', label: 'Biweekly Rotation', icon: '' },
    // { id: 'ai-learning', label: 'AI Learning', icon: '' }, // Archived July 2026
    { id: 'rejection-analytics', label: 'Rejection Analytics', icon: '' },
    { id: 'entry-scores', label: 'Entry Scores', icon: '' },
    { id: 'simulation', label: 'Simulation', icon: '' }
  ];
  
  // Persist active tab to localStorage and URL
  const getInitialTab = (): TabType => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromUrl = urlParams.get('tab') as TabType | null;
    if (tabFromUrl && tabs.some(t => t.id === tabFromUrl)) {
      return tabFromUrl;
    }
    const savedTab = localStorage.getItem('adminConsoleActiveTab') as TabType | null;
    if (savedTab && tabs.some(t => t.id === savedTab)) {
      return savedTab;
    }
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());
  const [policyVersion, setPolicyVersion] = useState<string>('');
  const [regimeStatus, setRegimeStatus] = useState<string>('neutral');

  // Update URL and localStorage when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    localStorage.setItem('adminConsoleActiveTab', tab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const [metricsRes, tradingSuccessRes, activityRes, policyRes] = await Promise.all([
        api.get('/admin/metrics'),
        api.get('/admin/trading-success-rate'),
        api.get('/admin/recent-activity'),
        api.get('/admin/policy')
      ]);
      setMetrics(metricsRes.data.data);
      setTradingSuccessRate(tradingSuccessRes.data.data);
      setRecentActivity(activityRes.data.data || []);
      setPolicyVersion(policyRes.data.data.version || '');
      
      // Extract regime status from metrics
      const regimeLabel = metricsRes.data.data?.regimeSignals?.regime_label || 'neutral';
      setRegimeStatus(regimeLabel);
    } catch (e: any) {
      setError(e?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            {/* Status Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="card bg-neutral-900 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Trading Status</h3>
                    <p className={`text-2xl font-bold ${metrics?.systemStatus === 'RUNNING' ? 'text-green-400' : metrics?.systemStatus === 'STOPPED' ? 'text-red-400' : 'text-yellow-400'}`}>{metrics?.systemStatus || 'UNKNOWN'}</p>
                  </div>
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                </div>
              </div>

              <div className="card bg-neutral-900 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Total Users</h3>
                    <p className="text-2xl font-bold text-cyan-400">{metrics?.totalUsers ?? 0}</p>
                    <p className="text-sm text-neutral-400 mt-1">
                      {metrics?.usersWithAlpacaKeys ?? 0} with Alpaca keys
                    </p>
                  </div>
                  <div className="w-3 h-3 bg-cyan-400 rounded-full"></div>
                </div>
              </div>

              <div className="card bg-neutral-900 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Portfolio Value</h3>
                    <p className="text-2xl font-bold text-blue-400">${metrics?.portfolioValue?.toLocaleString() ?? '0'}</p>
                  </div>
                  <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                </div>
              </div>

              <div className="card bg-neutral-900 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Trading Success Rate</h3>
                    <p className="text-2xl font-bold text-emerald-400">
                      {tradingSuccessRate?.successRate ? `${tradingSuccessRate.successRate.toFixed(1)}%` : '0%'}
                    </p>
                    <p className="text-sm text-neutral-400 mt-1">
                      {tradingSuccessRate?.profitableTrades || 0} / {tradingSuccessRate?.totalTrades || 0} profitable trades
                    </p>
                  </div>
                  <div className="w-3 h-3 bg-emerald-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card bg-neutral-900 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate('/logs')} className="w-full btn btn-outline text-left px-4 py-3">
                    View System Logs
                  </button>
                  <button onClick={async () => { const res = await api.get('/logs/export', { responseType: 'blob' as any }); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = 'reports.zip'; document.body.appendChild(a); a.click(); a.remove(); }} className="w-full btn btn-outline text-left px-4 py-3">
                    Export Reports
                  </button>
                  <button onClick={() => navigate('/admin/users')} className="w-full btn btn-outline text-left px-4 py-3">
                    Manage Users
                  </button>
                  <button onClick={() => navigate('/admin/system-health')} className="w-full btn btn-outline text-left px-4 py-3">
                    System Health Monitor
                  </button>
                  <button onClick={() => navigate('/settings')} className="w-full btn btn-outline text-left px-4 py-3">
                    System Settings
                  </button>
                  <button
                    onClick={async () => { await api.post('/admin/providers/test-global-apis'); await fetchMetrics(); }}
                    className="w-full btn btn-primary text-left px-4 py-3"
                  >
                    Test Global APIs (Daily Limit)
                  </button>
                </div>
              </div>

              <div className="card bg-neutral-900 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {recentActivity.length > 0 ? (
                    recentActivity.slice(0, 4).map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{activity.icon}</span>
                          <span className="text-neutral-300">{activity.description}</span>
                        </div>
                        <span className="text-neutral-500">
                          {new Date(activity.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-neutral-400">No recent activity</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Enhanced Admin Features */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* User/Account Health Table */}
              <div className="card bg-neutral-900 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">User Health</h3>
                  <span className="text-sm text-neutral-400">{metrics?.userHealth?.length || 0} users</span>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-300">
                        <th className="py-2 pr-2">User</th>
                        <th className="py-2 pr-2">Role</th>
                        <th className="py-2 pr-2">Accounts</th>
                        <th className="py-2 pr-2">Last Trade</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {(metrics?.userHealth || []).map((user: any) => (
                        <tr key={user.id}>
                          <td className="py-2 pr-2 text-white">
                            <div>
                              <div className="font-medium">{user.firstName} {user.lastName}</div>
                              <div className="text-xs text-neutral-400">{user.email}</div>
                            </div>
                          </td>
                          <td className="py-2 pr-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${user.role === 'ADMIN' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'}`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="py-2 pr-2 text-neutral-300">{user.accounts?.length || 0}</td>
                          <td className="py-2 pr-2 text-neutral-300">
                            {user.accounts?.[0]?.trades?.[0]?.createdAt ? 
                              new Date(user.accounts[0].trades[0].createdAt).toLocaleDateString() : 
                              'Never'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Queue/Worker Status */}
              <div className="card bg-neutral-900 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">System Status</h3>
                  <span className={`w-2 h-2 rounded-full ${metrics?.queueStatus?.active ? 'bg-green-400' : 'bg-red-400'}`}></span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-300">Queue Status</span>
                    <span className={metrics?.queueStatus?.active ? 'text-green-400' : 'text-red-400'}>
                      {metrics?.queueStatus?.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-300">Pending Jobs</span>
                    <span className="text-white">{metrics?.queueStatus?.pendingJobs || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-300">Completed Jobs</span>
                    <span className="text-green-400">{metrics?.queueStatus?.completedJobs || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-300">Failed Jobs</span>
                    <span className="text-red-400">{metrics?.queueStatus?.failedJobs || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-300">Audit Logs (24h)</span>
                    <span className="text-white">{metrics?.auditLogsCount || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* API Metrics */}
            <div className="card bg-neutral-900 p-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">API Metrics</h3>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-neutral-400">
                    Schedule: 1:30 PM UTC ({metrics?.apiMetrics?.nextRunAt ? 
                      new Date(metrics.apiMetrics.nextRunAt).toLocaleString('en-US', {
                        timeZone: 'America/New_York',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }) + ' EST' : 'Daily'}
                    )
                  </span>
                  <button onClick={fetchMetrics} className="btn btn-outline px-4 py-2">Refresh</button>
                </div>
              </div>
              {loading ? (
                <p className="text-neutral-400">Loading metrics...</p>
              ) : error ? (
                <p className="text-red-400">{error}</p>
              ) : (
                <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <p className="text-sm text-neutral-400">Last Provider Check</p>
                    {metrics?.apiMetrics?.lastRunAt ? (
                      <>
                        <p className="text-lg font-bold">
                          {new Date(metrics.apiMetrics.lastRunAt).toLocaleString('en-US', {
                            timeZone: 'UTC',
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                        <p className="text-xs text-neutral-500 mt-1">
                          EST: {new Date(metrics.apiMetrics.lastRunAt).toLocaleString('en-US', {
                            timeZone: 'America/New_York',
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                      </>
                    ) : (
                      <p className="text-xl font-bold text-yellow-400">Never</p>
                    )}
                    <p className="text-xs text-neutral-500 mt-1">Daily automated check</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Next Check</p>
                    {metrics?.apiMetrics?.nextRunAt ? (
                      <>
                        <p className="text-lg font-bold">
                          {new Date(metrics.apiMetrics.nextRunAt).toLocaleString('en-US', {
                            timeZone: 'UTC',
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                        <p className="text-xs text-neutral-500 mt-1">
                          EST: {new Date(metrics.apiMetrics.nextRunAt).toLocaleString('en-US', {
                            timeZone: 'America/New_York',
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                      </>
                    ) : (
                      <p className="text-xl font-bold text-yellow-400">Not scheduled</p>
                    )}
                    <p className="text-xs text-neutral-500 mt-1">Schedule: 1:30 PM UTC daily</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Valid Keys</p>
                    <p className="text-xl font-bold text-green-400">{metrics?.apiMetrics?.valid ?? 0} / {metrics?.apiMetrics?.total ?? 0}</p>
                    <p className="text-xs text-neutral-500 mt-1">Cached results (24h)</p>
                  </div>
                </div>
                <div className="mb-6">
                  <div className="bg-neutral-800 rounded-lg p-4">
                    <p className="text-sm text-neutral-400 mb-2">Schedule Information</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-300">
                          <span className="font-semibold">Cron:</span> {metrics?.apiMetrics?.schedule || '30 13 * * *'}
                        </p>
                        <p className="text-neutral-300">
                          <span className="font-semibold">Timezone:</span> {metrics?.apiMetrics?.timezone || 'UTC'}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-300">
                          <span className="font-semibold">Cache Duration:</span> 24 hours
                        </p>
                        <p className="text-neutral-300">
                          <span className="font-semibold">Description:</span> Daily API health check - 30 minutes before market open
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-300">
                        <th className="py-2 pr-4">Provider</th>
                        <th className="py-2 pr-4">Configured</th>
                        <th className="py-2 pr-4">Working</th>
                        <th className="py-2 pr-4">Message</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {(metrics?.providers || []).map((p: any) => (
                        <tr key={p.provider}>
                          <td className="py-2 pr-4 text-white">{p.provider}</td>
                          <td className="py-2 pr-4">
                            <span className={p.configured ? 'text-green-400' : 'text-red-400'}>{p.configured ? 'Yes' : 'No'}</span>
                          </td>
                          <td className="py-2 pr-4">
                            <span className={p.working ? 'text-green-400' : 'text-red-400'}>{p.working ? 'OK' : 'Fail'}</span>
                          </td>
                          <td className="py-2 pr-4 text-neutral-300">{p.message || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          </>
        );
      case 'performance':
        return <ProviderPerformanceCharts />;
      case 'features':
        return <div className="text-center text-neutral-400 py-8">Feature Flags archived - July 2026</div>;
      case 'rate-limits':
        return <RateLimitConfig />;
      case 'selectors':
        return (
          <div className="space-y-6">
            <div className="card bg-neutral-900 p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Policy {policyVersion} Selectors</h2>
              <div className="flex items-center gap-4 mb-6">
                <p className="text-neutral-300">
                  Advanced stock selection and monitoring tools for sophisticated trading strategies.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-400">Current Regime:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    regimeStatus === 'risk_on' ? 'bg-green-900 text-green-300' :
                    regimeStatus === 'risk_off' ? 'bg-red-900 text-red-300' :
                    'bg-yellow-900 text-yellow-300'
                  }`}>
                    {regimeStatus.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Market Screening</h4>
                  <p className="text-neutral-300 text-sm mb-4">
                    Real-time stock screening with Trading Universe integration. View screening results, entry scores, and trigger manual screening.
                  </p>
                  <Link 
                    to="/admin/market-screening" 
                    className="inline-block bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors"
                  >
                    View Market Screening
                  </Link>
                </div>
                
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Trading Universe</h4>
                  <p className="text-neutral-300 text-sm mb-4">
                    Sophisticated multi-factor stock selection system with regime-aware weighting and sector diversification.
                  </p>
                  <Link 
                    to="/admin/trading-universe" 
                    className="inline-block bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors"
                  >
                    View Trading Universe
                  </Link>
                </div>
                
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Earnings Watchlist</h4>
                  <p className="text-neutral-300 text-sm mb-4">
                    Monitoring-only list of top 20 earnings-day movers with risk distribution and multi-factor scoring.
                  </p>
                  <Link 
                    to="/admin/earnings-watchlist" 
                    className="inline-block bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors"
                  >
                    View Earnings Watchlist
                  </Link>
                </div>
                
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">QA Acceptance Checks</h4>
                  <p className="text-neutral-300 text-sm mb-4">
                    Automated quality assurance with 6 comprehensive rules for validating selector performance.
                  </p>
                  <Link 
                    to="/admin/qa-results" 
                    className="inline-block bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors"
                  >
                    View QA Results
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      case 'policy':
        return <PolicyConfigDashboard />;
      case 'queue':
        return <QueueDashboard />;
      case 'rotation':
        return <BiweeklyRotationDashboard />;
      // case 'ai-learning': // Archived July 2026
      //   return <AILearningDashboard />;
      case 'rejection-analytics':
        return <RejectionAnalytics />;
      case 'entry-scores':
        return <EntryScoreDistribution />;
      case 'simulation':
        return <SimulationDashboard />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <button onClick={() => window.history.back()} className="btn btn-outline px-4 py-2">← Back to Admin</button>
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Console</h1>
          <p className="text-neutral-400">
            System controls and monitoring for {user?.firstName} {user?.lastName}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-neutral-900 p-1 rounded-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as TabType)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-neutral-800 text-white'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminConsole;
